import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { useAsaPersonality } from '@/hooks/useAsaPersonality';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import AsaAssistant from '@/components/shared/AsaAssistant';
import { Landmark, Users, CheckCircle2, Wallet, AlertTriangle, ArrowUpRight, ArrowDownRight, Activity, HeartPulse, Package2, Package, Check, Calendar, CreditCard, Truck, FileText, Sparkles, ExternalLink, RefreshCw, X, AlertCircle, Eye, Clock, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import CustomDatePicker from '@/components/shared/CustomDatePicker';
import { PaymentApprovalModal } from '@/components/finance/PaymentApprovalModal';
import SuratJalanPrint from '@/Components/production/SuratJalanPrint';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function FinanceDashboardMain() {
  const { member: currentMember } = useSession();
  const { sapaan: asaNickname, gaya: asaGaya } = useAsaPersonality();
  const queryClient = useQueryClient();
  const [confirmingRejectId, setConfirmingRejectId] = useState(null);

  // ── Cash Flow Filter State ──
  const [cashFlowPeriod, setCashFlowPeriod] = useState('Semua');
  const [cashFlowDateStart, setCashFlowDateStart] = useState('');
  const [cashFlowDateEnd, setCashFlowDateEnd] = useState('');

  // ── Modal State ──
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [selectedPendingPayment, setSelectedPendingPayment] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // ── Surat Jalan Sync & Print State ──
  const [selectedSuratJalanForSync, setSelectedSuratJalanForSync] = useState(null);
  const [printSuratJalanData, setPrintSuratJalanData] = useState(null);
  const [previewSuratJalan, setPreviewSuratJalan] = useState(null);
  const [isUpdatingInvoice, setIsUpdatingInvoice] = useState(false);

  // ── Data Fetching ──
  const { data: companySettingsList = [] } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => base44.entities.CompanySetting.list(),
    staleTime: 60000,
  });
  const companySettings = companySettingsList?.[0] || {};
  const { clientPOs = [] } = useClientPOs();
  const { accountsMap: accounts } = useBankAccounts();

  const { data: productionOrders = [] } = useQuery({
    queryKey: ['production-orders-finance'],
    queryFn: () => base44.entities.ProductionOrder.list('-created_date'),
    staleTime: 30000,
  });

  const { data: payrollData = [] } = useQuery({
    queryKey: ['employee-payroll'],
    queryFn: () => base44.entities.EmployeePayroll.list(),
    staleTime: 30000,
  });

  const { data: mutations = [] } = useQuery({
    queryKey: ['account-mutations'],
    queryFn: () => base44.entities.AccountMutation.list('-date'),
    staleTime: 30000,
  });

  const { data: riwayatList = [] } = useQuery({
    queryKey: ['riwayat-produksi'],
    queryFn: () => base44.entities.RiwayatProduksi.list('-created_date'),
    staleTime: 30000,
  });

  const { data: expenses = [] } = useQuery({
    queryKey: ['operational-expenses'],
    queryFn: () => base44.entities.OperationalExpense.list('-created_date'),
    staleTime: 30000,
  });

  const { data: purchasingReqs = [] } = useQuery({
    queryKey: ['purchasing-requests'],
    queryFn: () => base44.entities.PurchasingRequest.list('-created_date'),
    staleTime: 30000,
  });

  const { data: allTasks = [] } = useQuery({
    queryKey: ['production-tasks-all'],
    queryFn: () => base44.entities.ProductionTask.list('-created_date'),
    staleTime: 30000,
  });

  const pendingPayments = useMemo(() => {
    const list = [];
    const seenKeys = new Set();

    // 1. Client PO payments waiting for finance confirmation
    (clientPOs || []).forEach(po => {
      const riwayat = po.riwayatPembayaran || [];
      riwayat.forEach((r, idx) => {
        if (r.status_konfirmasi === 'Menunggu Konfirmasi Finance' || r.status_konfirmasi === 'Menunggu Konfirmasi' || r.status_konfirmasi === 'pending') {
          const key = `po-${po.id}-${idx}-${r.jumlah}`;
          seenKeys.add(key);
          list.push({
            id: key,
            rawPaymentId: r.id || idx,
            paymentIndex: idx,
            invoice_id: po.id,
            invoice_type: 'client_po',
            invoice_name: po.customerName || po.client || po.id,
            brand_name: po.brandName || po.customerName || po.id,
            jumlah: Number(r.jumlah) || 0,
            tanggal_bayar: r.tanggal_bayar || r.tanggal || po.date || '-',
            diinput_oleh: r.diinput_oleh || po.pic || 'Tim Marketing',
            bank_tujuan: r.bank_tujuan || po.paymentMethod || 'BCA',
            catatan: r.catatan || r.keterangan || '',
            status: 'pending',
            rawRecord: po,
            rawPayment: r,
          });
        }
      });
    });

    // 2. HPP / RiwayatProduksi payments waiting for finance confirmation
    (riwayatList || []).forEach(hpp => {
      const riwayat = hpp.riwayat_pembayaran || [];
      riwayat.forEach((r, idx) => {
        if (r.status_konfirmasi === 'Menunggu Konfirmasi Finance' || r.status_konfirmasi === 'Menunggu Konfirmasi' || r.status_konfirmasi === 'pending') {
          const key = `hpp-${hpp.id}-${idx}-${r.jumlah}`;
          seenKeys.add(key);
          list.push({
            id: key,
            rawPaymentId: r.id || idx,
            paymentIndex: idx,
            invoice_id: hpp.id,
            invoice_type: 'hpp',
            invoice_name: hpp.nama_brand || `HPP-${hpp.id}`,
            brand_name: hpp.nama_brand || '-',
            jumlah: Number(r.jumlah) || 0,
            tanggal_bayar: r.tanggal_bayar || r.tanggal || hpp.tanggal || '-',
            diinput_oleh: r.diinput_oleh || 'Tim Produksi',
            bank_tujuan: r.bank_tujuan || 'BCA',
            catatan: r.catatan || r.keterangan || '',
            status: 'pending',
            rawRecord: hpp,
            rawPayment: r,
          });
        }
      });
    });

    // 3. AccountMutation pending items that might not be in POs
    (mutations || []).forEach((m, idx) => {
      if (m.status === 'pending' && m.invoice_id) {
        const key = `mut-${m.id || idx}-${m.jumlah || m.amount}`;
        if (!seenKeys.has(key)) {
          list.push({
            id: m.id || key,
            mutation_id: m.id,
            invoice_id: m.invoice_id,
            invoice_type: m.invoice_type || 'client_po',
            invoice_name: m.invoice_name || m.description || 'Invoice',
            jumlah: Number(m.jumlah || m.amount) || 0,
            tanggal_bayar: m.tanggal_bayar || m.date || '-',
            diinput_oleh: m.diinput_oleh || 'User',
            bank_tujuan: m.bank_tujuan || m.account || 'BCA',
            catatan: m.catatan || m.description || '',
            status: 'pending',
            rawMutation: m,
          });
        }
      }
    });

    return list;
  }, [clientPOs, riwayatList, mutations]);

  const unconfirmedRejects = useMemo(() => {
    const rejects = [];
    (allTasks || []).forEach(task => {
      if (!task.member_progress) return;
      let mp = task.member_progress;
      if (typeof mp === 'string') {
        try { mp = JSON.parse(mp); } catch(e) { return; }
      }
      Object.entries(mp).forEach(([memberId, prog]) => {
        if (prog.status !== 'completed' || !prog.rejected_items) return;
        let rjItems = prog.rejected_items;
        if (typeof rjItems === 'string') {
          try { rjItems = JSON.parse(rjItems); } catch(e) { rjItems = []; }
        }
        if (!Array.isArray(rjItems)) return;
        
        rjItems.forEach((rj, rjIdx) => {
          let items = rj.items;
          if (typeof items === 'string') {
            try { items = JSON.parse(items); } catch(e) { items = []; }
          }
          if (!Array.isArray(items)) return;
          
          items.forEach((item, itemIdx) => {
            if (!item.name || !item.qty) return;
            if (!item.is_confirmed_by_finance) {
              rejects.push({
                id: `${task.id}__${memberId}__${rjIdx}__${itemIdx}`,
                taskId: task.id,
                memberId,
                rjIdx,
                itemIdx,
                taskName: task.task_name,
                itemName: item.name,
                qty: Number(item.qty) || 0,
                notes: item.notes || '',
              });
            }
          });
        });
      });
    });
    return rejects;
  }, [allTasks]);

  const handleConfirmReject = async (rj) => {
    setConfirmingRejectId(rj.id);
    try {
      const task = allTasks.find(t => t.id === rj.taskId);
      if (task) {
        const mp = JSON.parse(JSON.stringify(task.member_progress || {}));
        const prog = mp[rj.memberId];
        if (prog?.rejected_items?.[rj.rjIdx]?.items?.[rj.itemIdx]) {
          prog.rejected_items[rj.rjIdx].items[rj.itemIdx].is_confirmed_by_finance = true;
          await base44.entities.ProductionTask.update(task.id, { member_progress: mp });
          queryClient.invalidateQueries({ queryKey: ['production-tasks-all'] });
          toast.success('Reject berhasil dikonfirmasi');
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Gagal mengkonfirmasi reject');
    }
    setConfirmingRejectId(null);
  };

  // ── Surat Jalan & Pengiriman Produksi (Multi-Variant Support) ──
  const suratJalanList = useMemo(() => {
    const list = [];
    (productionOrders || []).forEach(po => {
      if (!po.shipments || !po.shipments.length) return;
      if (po.is_surat_jalan_confirmed_finance) return;
      
      const bName = (po.brand_name || '').toLowerCase();
      const isOldOrder = (po.created_date && new Date(po.created_date) < new Date('2026-08-01')) || (po.created_at && new Date(po.created_at) < new Date('2026-08-01'));
      
      // Filter out past historical orders (e.g. Muv from June, old completed orders), focus strictly on active POs (e.g. Earthora)
      if ((isOldOrder || po.status === 'done') && !bName.includes('earthora')) {
        return;
      }
      
      const linkedPO = (clientPOs || []).find(c => 
        c.id === po.id || 
        c.id === po.order_number || 
        c.order_number === po.id || 
        c.order_number === po.order_number
      );

      const baseProducts = (linkedPO?.products && linkedPO.products.length > 0)
        ? linkedPO.products
        : (po.products || []);

      // Calculate shipped qty per variant across all shipments
      const shippedPerVariant = {};
      (po.shipments || []).forEach(s => {
        (s.items || []).forEach(item => {
          const vName = (item.variant_name || '').trim();
          if (vName) {
            shippedPerVariant[vName] = (shippedPerVariant[vName] || 0) + (Number(item.qty_akhir) || 0);
          }
        });
      });

      // Build variants breakdown
      let countMatch = 0;
      let countSurplus = 0;
      let countPartial = 0;
      let countUnshipped = 0;
      let totalSurplusQty = 0;
      let totalSurplusNominal = 0;
      let totalPartialQty = 0;
      let totalNetNominal = 0;

      const variantsBreakdown = baseProducts.map((p, pIdx) => {
        const vName = (p.product_name || p.variant_name || `Varian ${pIdx + 1}`).trim();
        const invoiceQty = Number(p.qty) || Number(p.target_qty) || 0;
        
        let shippedQty = 0;
        if (baseProducts.length === 1 && Object.keys(shippedPerVariant).length > 0) {
          shippedQty = Object.values(shippedPerVariant).reduce((a, b) => a + b, 0);
        } else {
          shippedQty = shippedPerVariant[vName] || 0;
          if (shippedQty === 0) {
            const matchedKey = Object.keys(shippedPerVariant).find(k => k.toLowerCase().includes(vName.toLowerCase()) || vName.toLowerCase().includes(k.toLowerCase()));
            if (matchedKey) shippedQty = shippedPerVariant[matchedKey];
          }
        }

        const diff = shippedQty - invoiceQty;
        const unitPrice = Number(p.harga_satuan) || Number(p.price) || (invoiceQty > 0 && linkedPO?.total_harga_jual ? Math.round(linkedPO.total_harga_jual / invoiceQty) : 0);
        const selisihNominal = diff * unitPrice;

        let status = 'match';
        if (shippedQty === invoiceQty) {
          status = 'match';
          countMatch++;
        } else if (shippedQty > invoiceQty) {
          status = 'surplus';
          countSurplus++;
          totalSurplusQty += diff;
          totalSurplusNominal += selisihNominal;
        } else if (shippedQty > 0 && shippedQty < invoiceQty) {
          status = 'partial';
          countPartial++;
          totalPartialQty += Math.abs(diff);
        } else if (shippedQty === 0) {
          status = 'unshipped';
          countUnshipped++;
        }

        totalNetNominal += selisihNominal;

        return {
          id: pIdx,
          variant_name: vName,
          invoice_qty: invoiceQty,
          shipped_qty: shippedQty,
          diff,
          unit_price: unitPrice,
          selisih_nominal: selisihNominal,
          status,
        };
      });

      const totalShipped = (po.shipments || []).reduce((sum, s) => {
        return sum + (s.items || []).reduce((s2, item) => s2 + (Number(item.qty_akhir) || 0), 0);
      }, 0);

      const targetQty = baseProducts.reduce((sum, p) => sum + (Number(p.qty) || Number(p.target_qty) || 0), 0);
      const invoiceQty = targetQty;
      const diff = totalShipped - invoiceQty;
      const isMatch = countSurplus === 0 && countPartial === 0 && countUnshipped === 0 && diff === 0;

      const firstProd = baseProducts[0];
      const unitPrice = Number(firstProd?.harga_satuan) || Number(firstProd?.price) || (invoiceQty > 0 && linkedPO?.total_harga_jual ? Math.round(linkedPO.total_harga_jual / invoiceQty) : 0);
      const selisihNominal = totalSurplusNominal > 0 ? totalSurplusNominal : (diff * unitPrice);

      let currentTagihan = Number(linkedPO?.total_harga_jual || linkedPO?.total_harga_jual_semua || linkedPO?.total_jual_po || 0);
      if (currentTagihan === 0 && baseProducts.length > 0) {
        currentTagihan = baseProducts.reduce((sum, p) => {
          const pQty = Number(p.qty) || Number(p.target_qty) || 0;
          const pPrice = Number(p.harga_satuan) || Number(p.price) || 0;
          return sum + (pQty * pPrice);
        }, 0);
      }
      if (currentTagihan === 0 && unitPrice > 0 && invoiceQty > 0) {
        currentTagihan = invoiceQty * unitPrice;
      }
      const newTagihan = currentTagihan + (totalSurplusNominal > 0 ? totalSurplusNominal : selisihNominal);

      (po.shipments || []).forEach((shipment, sIdx) => {
        const shipmentQty = (shipment.items || []).reduce((sum, it) => sum + (Number(it.qty_akhir) || 0), 0);
        const shipmentDate = shipment.date || po.shipped_at || po.updated_date;
        const isShipmentConfirmed = shipment.is_confirmed_finance || po.is_surat_jalan_confirmed_finance || false;

        list.push({
          id: `${po.id}-${sIdx}`,
          po_id: po.id,
          order_number: po.order_number || po.id,
          brand_name: po.brand_name || linkedPO?.brand_name || linkedPO?.client || 'Brand',
          product_name: firstProd?.product_name || firstProd?.variant_name || po.brand_name || 'Produk',
          pic_marketing: linkedPO?.pic_brand || linkedPO?.pic || linkedPO?.pic_marketing || po.pic_marketing || 'Tim Marketing',
          invoice_id: linkedPO?.id || po.order_number || po.id,
          invoice_date: linkedPO?.date || linkedPO?.created_date || po.created_date,
          shipped_date: shipmentDate,
          shipments: po.shipments,
          currentShipment: shipment,
          shipmentNumber: sIdx + 1,
          totalShipmentsInPO: po.shipments.length,
          shipmentQty,
          totalShipped,
          invoiceQty,
          targetQty,
          diff,
          isMatch,
          unitPrice,
          selisihNominal,
          currentTagihan,
          newTagihan,
          linkedPO,
          productionOrder: po,
          isConfirmedFinance: isShipmentConfirmed,
          variantsBreakdown,
          countMatch,
          countSurplus,
          countPartial,
          countUnshipped,
          totalSurplusQty,
          totalSurplusNominal,
          totalPartialQty,
        });
      });
    });
    return list;
  }, [productionOrders, clientPOs]);

  const handleApplySuratJalanToInvoice = async (sj) => {
    setIsUpdatingInvoice(true);
    try {
      const linkedPO = sj.linkedPO;
      if (!linkedPO) {
        toast.error('Invoice terkait tidak ditemukan di sistem.');
        return;
      }

      let updatedProducts = [...(linkedPO.products || [])];
      if (updatedProducts.length > 0) {
        if (updatedProducts.length === 1) {
          updatedProducts[0] = {
            ...updatedProducts[0],
            qty: sj.totalShipped,
            target_qty: sj.totalShipped,
          };
        } else {
          // Update each variant that has surplus or specific shipped qty
          (sj.variantsBreakdown || []).forEach(vb => {
            const pIdx = updatedProducts.findIndex(p => (p.product_name || p.variant_name || '').trim().toLowerCase() === vb.variant_name.toLowerCase());
            if (pIdx !== -1) {
              // If surplus or updated, update qty
              if (vb.shipped_qty > 0) {
                updatedProducts[pIdx] = {
                  ...updatedProducts[pIdx],
                  qty: vb.shipped_qty,
                  target_qty: vb.shipped_qty,
                };
              }
            }
          });
        }
      }

      let newTotalJual = 0;
      updatedProducts.forEach(p => {
        const pQty = Number(p.qty) || Number(p.target_qty) || 0;
        const pPrice = Number(p.harga_satuan) || Number(p.price) || sj.unitPrice || 0;
        newTotalJual += pQty * pPrice;
      });
      if (newTotalJual === 0 && sj.unitPrice > 0) {
        newTotalJual = sj.totalShipped * sj.unitPrice;
      }

      const nominalPPN = linkedPO.ppn_mode === 'exclude' ? (newTotalJual * (parseFloat(linkedPO.ppn_percentage) || 0) / 100) : 0;
      const totalTagihan = newTotalJual + nominalPPN;
      const jumlahDibayar = linkedPO.jumlahDibayar || 0;
      const sisaTagihan = Math.max(0, totalTagihan - jumlahDibayar);
      const statusBayar = jumlahDibayar >= totalTagihan ? 'LUNAS' : (jumlahDibayar > 0 ? `DP ${Math.round((jumlahDibayar/totalTagihan)*100)}%` : 'BELUM BAYAR');

      await base44.entities.ClientPO.update(linkedPO.id, {
        products: updatedProducts,
        total_harga_jual: newTotalJual,
        total_harga_jual_semua: newTotalJual,
        total_jual_po: newTotalJual,
        sisa_piutang: sisaTagihan,
        statusBayar: statusBayar,
        surat_jalan_sync_note: `[Sync Surat Jalan] Qty varian disesuaikan berdasarkan Surat Jalan Produksi pada ${new Date().toLocaleDateString('id-ID')}`,
      });

      if (sj.productionOrder?.id) {
        await base44.entities.ProductionOrder.update(sj.productionOrder.id, {
          is_surat_jalan_confirmed_finance: true,
          surat_jalan_synced_at: new Date().toISOString(),
          products: updatedProducts,
        }).catch(() => {});
      }

      toast.success(`🎉 Invoice #${sj.invoice_id} berhasil disinkronkan dengan rincian varian terbaru!`);
      queryClient.invalidateQueries();
      setSelectedSuratJalanForSync(null);
    } catch (err) {
      console.error(err);
      toast.error('Gagal mengupdate invoice: ' + err.message);
    } finally {
      setIsUpdatingInvoice(false);
    }
  };

  const handleDismissSuratJalanSync = async (sj) => {
    try {
      if (sj.productionOrder?.id) {
        await base44.entities.ProductionOrder.update(sj.productionOrder.id, {
          is_surat_jalan_confirmed_finance: true,
          surat_jalan_dismissed_at: new Date().toISOString(),
        });
      }
      toast.success('Surat Jalan telah ditandai selesai dicek.');
      queryClient.invalidateQueries();
      setSelectedSuratJalanForSync(null);
    } catch (err) {
      toast.error('Gagal memperbarui status: ' + err.message);
    }
  };

  // ── Hutang Pembelian (Tempo & Cash) ──
  const { totalHutangTempo, countHutangJatuhTempo, countHutangMendekati, countBelumDibayar, totalBelumDibayar } = useMemo(() => {
    let hutang = 0;
    let jatuhTempoCount = 0;
    let mendekatiCount = 0;
    let belumDibayarCount = 0;
    let belumDibayarTotal = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    purchasingReqs.forEach(req => {
      if (req.metode_pembayaran === 'Tempo' && req.status !== 'Sudah Dibayar') {
        hutang += Number(req.total_biaya || 0);
        if (req.tanggal_jatuh_tempo && req.status === 'Menunggu Pembayaran') {
          const tempoDate = new Date(req.tanggal_jatuh_tempo);
          const diffDays = Math.ceil((tempoDate - today) / (1000 * 60 * 60 * 24));
          if (diffDays < 0) jatuhTempoCount++;
          else if (diffDays >= 0 && diffDays <= 3) mendekatiCount++;
        }
      }
      if (req.status === 'Menunggu Pembayaran') {
        belumDibayarCount++;
        belumDibayarTotal += Number(req.total_biaya || 0);
      }
    });
    return { totalHutangTempo: hutang, countHutangJatuhTempo: jatuhTempoCount, countHutangMendekati: mendekatiCount, countBelumDibayar: belumDibayarCount, totalBelumDibayar: belumDibayarTotal };
  }, [purchasingReqs]);

  const { totalUangMasuk, totalHPP, totalMargin, marginPct, totalPiutang, countPiutang, totalTagihan, piutangSelesai, countPiutangSelesai } = useMemo(() => {
    let uangMasuk = 0;
    let hpp = 0;
    let piutang = 0;
    let countP = 0;
    let piutangSelesai = 0;
    let countPSelesai = 0;
    let tagihan = 0;

    clientPOs.forEach(po => {
      if (po.poMode === 'sph') return;
      const prodJual = (po.products || []).reduce((s, p) => s + (p.total_harga_jual || 0), 0);
      const legJual = (po.legalitas || []).reduce((s, l) => s + ((l.harga_jual || 0) * (l.qty_variant || 1)), 0);
      const nominalPPN = po.ppn_mode === 'exclude' ? ((prodJual + legJual) * (parseFloat(po.ppn_percentage) || 0) / 100) : 0;
      const totalJual = prodJual + legJual; // Omset
      const totalTagihan = totalJual + nominalPPN;

      const prodHpp = (po.products || []).reduce((s, p) => {
        const qty = p.qty || 1;
        const modalPackaging = (p.botol_harga_beli || 0) + (p.tutup_harga_beli || 0) + (p.spray_harga_beli || 0);
        const volume = p.botol_ukuran_client || p.botol_ukuran_label || p.ukuran_botol_ml || p.volume_ml || 0;
        let racikan = p.racikan || [];
        if (!Array.isArray(racikan)) { try { racikan = JSON.parse(racikan); } catch(e) { racikan = []; } if (!Array.isArray(racikan)) racikan = []; }
        const modalRacikan = racikan.reduce((rs, r) => { const c = r.harga_per_satuan || 0; const v = volume * ((r.persentase || 0) / 100); return rs + v * (c / 1000); }, 0);
        let kt = p.komponen_tambahan || [];
        if (!Array.isArray(kt)) { try { kt = JSON.parse(kt); } catch(e) { kt = []; } if (!Array.isArray(kt)) kt = []; }
        const modalKomponen = kt.reduce((ks, k) => ks + (k.harga || 0), 0);
        const jasaNominal = p.jasa_nominal || p.final_jasa_nominal || 0;
        const hppPerPcs = modalPackaging + modalRacikan + modalKomponen + jasaNominal;
        const calc = hppPerPcs * qty;
        if (calc > 0) return s + calc;
        return s + (p.total_hpp_product || Math.round((p.total_harga_jual || 0) * 0.65));
      }, 0);
      const legHpp = (po.legalitas || []).reduce((s, l) => s + ((l.harga_beli || 0) * (l.qty_variant || 1)), 0);
      const totalHppPO = prodHpp + legHpp;
      const totalJasaClient = (po.products || []).reduce((sum, p) => sum + ((p.jasa_nominal || p.final_jasa_nominal || 0) * (p.qty || 1)), 0);
      const hppTanpaJasa = totalHppPO - totalJasaClient;

      const totalBayar = (po.riwayatPembayaran || []).reduce((s, r) => s + (r.jumlah || 0), 0);
      const statusBayar = String(po.statusBayar || '');
      const isSelesai = String(po.status || '').toUpperCase() === 'SELESAI';

      if (statusBayar === 'LUNAS') {
        uangMasuk += totalJual;
        hpp += hppTanpaJasa;
      } else if (totalBayar > 0) {
        uangMasuk += totalBayar;
        hpp += hppTanpaJasa;
        piutang += (totalTagihan - totalBayar);
        countP++;
        if (isSelesai) {
          piutangSelesai += (totalTagihan - totalBayar);
          countPSelesai++;
        }
      } else {
        tagihan += totalTagihan;
        hpp += hppTanpaJasa;
        if (totalTagihan > 0) {
          piutang += totalTagihan;
          countP++;
          if (isSelesai) {
            piutangSelesai += totalTagihan;
            countPSelesai++;
          }
        }
      }
    });

    riwayatList.forEach(r => {
      const prodJual = r.total_harga_jual_semua || 0;
      const nominalPPN = r.ppn_mode === 'exclude' ? (prodJual * (parseFloat(r.ppn_percentage) || 0) / 100) : 0;
      const totalJual = prodJual;
      const totalTagihan = totalJual + nominalPPN;
      
      const totalHppR = r.total_hpp_semua || 0;
      const dibayar = r.jumlah_dibayar || 0;
      const status = String(r.status_bayar || 'Belum Bayar');

      if (status === 'Sudah Lunas') {
        uangMasuk += totalJual;
        hpp += totalHppR;
      } else if (status === 'Belum Lunas' && dibayar > 0) {
        uangMasuk += dibayar;
        hpp += totalHppR;
        piutang += (totalTagihan - dibayar);
        countP++;
      } else {
        hpp += totalHppR;
        if (totalTagihan > 0) {
          piutang += (totalTagihan - dibayar);
          countP++;
        }
      }
    });

    uangMasuk = Math.round(uangMasuk);
    hpp = Math.round(hpp);
    const margin = uangMasuk - hpp;
    const pct = uangMasuk > 0 ? (margin / uangMasuk) * 100 : (hpp > 0 ? -100 : 0);
    return { 
      totalUangMasuk: uangMasuk, 
      totalHPP: hpp, 
      totalMargin: margin, 
      marginPct: pct, 
      totalPiutang: piutang, 
      countPiutang: countP, 
      totalTagihan: tagihan,
      piutangSelesai,
      countPiutangSelesai: countPSelesai
    };
  }, [clientPOs, riwayatList]);

  const { mutasiMasukBulanIni, mutasiKeluarBulanIni } = useMemo(() => {
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    let masuk = 0, keluar = 0;
    mutations.forEach(m => {
      const d = new Date(m.date);
      if (d.getMonth() === month && d.getFullYear() === year) {
        if (m.type === 'masuk') masuk += Number(m.amount || 0);
        if (m.type === 'keluar') keluar += Number(m.amount || 0);
      }
    });
    return { mutasiMasukBulanIni: masuk, mutasiKeluarBulanIni: keluar };
  }, [mutations]);

  const totalOpexBulanIni = useMemo(() => {
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    let total = 0;
    expenses.forEach(e => {
      const d = new Date(e.date || e.created_date);
      if (d.getMonth() === month && d.getFullYear() === year) {
        total += Number(e.amount || 0);
      }
    });
    return total;
  }, [expenses]);

  const { countBelumGaji, totalGajiBelum } = useMemo(() => {
    let count = 0, total = 0;
    const now = new Date();
    payrollData.forEach(emp => {
      if (emp.status === 'Belum Gajian' || (emp.next_reset_date && new Date(emp.next_reset_date) <= now)) {
        count++;
        total += Number(emp.base_salary || 0);
      }
    });
    return { countBelumGaji: count, totalGajiBelum: total };
  }, [payrollData]);

  const balances = useMemo(() => {
    const bal = {
      bca: Number(accounts.bca?.startBalance || 0),
      mandiri: Number(accounts.mandiri?.startBalance || 0),
      bni: Number(accounts.bni?.startBalance || 0)
    };
    mutations.forEach(m => {
      let accKey = null;
      const mAcc = (m.account || '').toLowerCase().trim();
      if (mAcc === (accounts.bca?.name || '').toLowerCase().trim()) accKey = 'bca';
      else if (mAcc === (accounts.mandiri?.name || '').toLowerCase().trim()) accKey = 'mandiri';
      else if (mAcc === (accounts.bni?.name || '').toLowerCase().trim()) accKey = 'bni';
      else if (mAcc.includes('bca')) accKey = 'bca';
      else if (mAcc.includes('mandiri') || mAcc.includes('qris')) accKey = 'mandiri';
      else if (mAcc.includes('bni')) accKey = 'bni';
      else accKey = 'bca';
      if (accKey) {
        if (m.type === 'masuk') bal[accKey] += Number(m.amount || 0);
        if (m.type === 'keluar') bal[accKey] -= Number(m.amount || 0);
      }
    });
    const total = bal.bca + bal.mandiri + bal.bni;
    return { ...bal, total };
  }, [accounts, mutations]);

  const cashFlowPerAccount = useMemo(() => {
    let cf = {
      bca: { masuk: 0, keluar: 0 },
      mandiri: { masuk: 0, keluar: 0 },
      bni: { masuk: 0, keluar: 0 }
    };
    
    let now = new Date();
    let startDate = new Date(0);
    let endDate = new Date('2099-12-31');

    if (cashFlowPeriod === 'Hari ini') {
      startDate = new Date();
      startDate.setHours(0,0,0,0);
      endDate = new Date();
      endDate.setHours(23,59,59,999);
    } else if (cashFlowPeriod === '7 Hari') {
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0,0,0,0);
      endDate = new Date();
      endDate.setHours(23,59,59,999);
    } else if (cashFlowPeriod === '30 Hari') {
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0,0,0,0);
      endDate = new Date();
      endDate.setHours(23,59,59,999);
    } else if (cashFlowPeriod === 'Bulan Ini') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      startDate.setHours(0,0,0,0);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      endDate.setHours(23,59,59,999);
    } else if (cashFlowPeriod === 'Custom') {
      if (cashFlowDateStart) startDate = new Date(cashFlowDateStart);
      if (cashFlowDateEnd) {
        endDate = new Date(cashFlowDateEnd);
        endDate.setHours(23,59,59,999);
      }
    }

    mutations.forEach(m => {
      const d = new Date(m.date);
      if (d >= startDate && d <= endDate) {
        let accKey = null;
        const mAcc = (m.account || '').toLowerCase().trim();
        if (mAcc === (accounts.bca?.name || '').toLowerCase().trim()) accKey = 'bca';
        else if (mAcc === (accounts.mandiri?.name || '').toLowerCase().trim()) accKey = 'mandiri';
        else if (mAcc === (accounts.bni?.name || '').toLowerCase().trim()) accKey = 'bni';
        else if (mAcc.includes('bca')) accKey = 'bca';
        else if (mAcc.includes('mandiri') || mAcc.includes('qris')) accKey = 'mandiri';
        else if (mAcc.includes('bni')) accKey = 'bni';
        
        if (accKey) {
          if (m.type === 'masuk') cf[accKey].masuk += Number(m.amount || 0);
          if (m.type === 'keluar') cf[accKey].keluar += Number(m.amount || 0);
        }
      }
    });

    return cf;
  }, [mutations, accounts, cashFlowPeriod, cashFlowDateStart, cashFlowDateEnd]);

  const healthScore = useMemo(() => {
    let score = 50; 
    if (marginPct >= 40) score += 25;
    else if (marginPct >= 30) score += 20;
    else if (marginPct >= 20) score += 10;
    else if (marginPct >= 10) score += 5;
    else if (marginPct < 0) score -= 15;

    const totalKewajiban = totalGajiBelum + totalOpexBulanIni;
    if (balances.total > totalKewajiban * 3) score += 20;
    else if (balances.total > totalKewajiban * 2) score += 15;
    else if (balances.total > totalKewajiban) score += 10;
    else if (balances.total > 0) score += 5;
    else score -= 10;

    if (mutasiMasukBulanIni > mutasiKeluarBulanIni * 1.5) score += 15;
    else if (mutasiMasukBulanIni > mutasiKeluarBulanIni) score += 10;
    else if (mutasiMasukBulanIni > mutasiKeluarBulanIni * 0.7) score += 5;
    else score -= 5;

    if (totalPiutang > totalUangMasuk * 0.5 && totalPiutang > 0) score -= 15;
    else if (totalPiutang > totalUangMasuk * 0.3 && totalPiutang > 0) score -= 8;
    else if (totalPiutang > 0) score -= 3;

    if (countBelumGaji > 3) score -= 10;
    else if (countBelumGaji > 0) score -= 5;

    return Math.max(0, Math.min(100, score));
  }, [marginPct, balances.total, totalGajiBelum, totalOpexBulanIni, mutasiMasukBulanIni, mutasiKeluarBulanIni, totalPiutang, totalUangMasuk, countBelumGaji]);

  const getHealthLabel = (s) => {
    if (s >= 80) return 'Sangat Sehat';
    if (s >= 60) return 'Sehat';
    if (s >= 40) return 'Waspada';
    if (s >= 20) return 'Kritis';
    return 'Darurat';
  };
  const getHealthColor = (s) => {
    if (s >= 80) return 'text-emerald-600';
    if (s >= 60) return 'text-blue-600';
    if (s >= 40) return 'text-amber-600';
    return 'text-rose-600';
  };
  const getHealthBg = (s) => {
    if (s >= 80) return 'from-emerald-500 to-emerald-700';
    if (s >= 60) return 'from-blue-500 to-blue-700';
    if (s >= 40) return 'from-amber-500 to-amber-700';
    return 'from-rose-500 to-rose-700';
  };
  const getHealthRingColor = (s) => {
    if (s >= 80) return '#10b981';
    if (s >= 60) return '#3b82f6';
    if (s >= 40) return '#f59e0b';
    return '#ef4444';
  };

  const runwayMonths = useMemo(() => {
    const avgKeluar = mutasiKeluarBulanIni > 0 ? mutasiKeluarBulanIni : (totalOpexBulanIni + totalGajiBelum) || 1;
    return balances.total / avgKeluar;
  }, [balances.total, mutasiKeluarBulanIni, totalOpexBulanIni, totalGajiBelum]);

  const asaInsights = useMemo(() => {
    const insights = [];
    if (healthScore >= 80) {
      insights.push({ id: 'health-great', type: 'info', text: `Kondisi keuangan **Sangat Sehat** (Skor ${healthScore}/100). Arus kas positif, margin terjaga, dan piutang terkendali. Keep it up! 💪` });
    } else if (healthScore >= 60) {
      insights.push({ id: 'health-ok', type: 'info', text: `Kondisi keuangan **Sehat** (Skor ${healthScore}/100). Secara keseluruhan stabil, namun ada beberapa area yang perlu diperhatikan.` });
    } else if (healthScore >= 40) {
      insights.push({ id: 'health-warn', type: 'warning', text: `⚠️ Kondisi keuangan **Waspada** (Skor ${healthScore}/100). Beberapa indikator menunjukkan tekanan pada arus kas. Perlu tindakan segera untuk stabilisasi.` });
    } else {
      insights.push({ id: 'health-critical', type: 'alert', text: `🚨 Kondisi keuangan **Kritis** (Skor ${healthScore}/100). Arus kas negatif dan/atau piutang sangat tinggi. Prioritaskan penagihan dan kurangi pengeluaran non-esensial SEKARANG!` });
    }

    if (marginPct < 0) {
      insights.push({ id: 'margin-neg', type: 'alert', text: `Margin dari uang masuk saat ini **negatif (${marginPct.toFixed(1)}%)**. Artinya total uang yang diterima belum bisa menutupi modal produksi. Segera tagih piutang atau evaluasi HPP!` });
    } else if (marginPct < 20 && totalUangMasuk > 0) {
      insights.push({ id: 'margin-low', type: 'warning', text: `Margin keuntungan hanya **${marginPct.toFixed(1)}%** — di bawah target sehat (≥30%). Pertimbangkan menaikkan harga jual atau menekan biaya bahan baku.` });
    }

    if (piutangSelesai > 0) {
      const piutangRatio = totalUangMasuk > 0 ? (piutangSelesai / totalUangMasuk * 100) : 100;
      if (piutangRatio > 50) {
        insights.push({ id: 'piutang-high', type: 'alert', text: `🔴 Piutang sangat tinggi: **Rp ${fmt(piutangSelesai)}** (${piutangRatio.toFixed(0)}% dari uang masuk). Ada **${countPiutangSelesai} invoice Selesai** belum lunas. Jika tidak segera ditagih, ini bisa mengganggu cash flow operasional dalam waktu dekat!` });
      } else if (piutangRatio > 25) {
        insights.push({ id: 'piutang-med', type: 'warning', text: `Piutang cukup besar: **Rp ${fmt(piutangSelesai)}** dari **${countPiutangSelesai} invoice Selesai**. Saya sarankan follow-up ke klien untuk mempercepat pembayaran agar kas tetap lancar.` });
      } else if (countPiutangSelesai > 0) {
        insights.push({ id: 'piutang-ok', type: 'info', text: `Ada **${countPiutangSelesai} invoice Selesai** dengan sisa tagihan **Rp ${fmt(piutangSelesai)}**. Masih dalam batas wajar, tapi tetap pantau jadwal jatuh temponya.` });
      }
    }

    if (runwayMonths < 1) {
      insights.push({ id: 'runway-crit', type: 'alert', text: `🚨 **Dana operasional hanya cukup untuk ${runwayMonths.toFixed(1)} bulan!** Berdasarkan rata-rata pengeluaran, kas akan habis dalam waktu dekat. Segera prioritaskan pemasukan atau potong biaya!` });
    } else if (runwayMonths < 3) {
      insights.push({ id: 'runway-warn', type: 'warning', text: `Dana operasional cukup untuk **~${runwayMonths.toFixed(1)} bulan** ke depan. Ini masih cukup ketat — pastikan ada invoice baru atau tagihan yang masuk untuk menjaga kestabilan.` });
    }

    const netCashFlow = mutasiMasukBulanIni - mutasiKeluarBulanIni;
    if (netCashFlow < 0) {
      insights.push({ id: 'cashflow-neg', type: 'warning', text: `Arus kas bulan ini **negatif** (Rp ${fmt(Math.abs(netCashFlow))} lebih banyak keluar). Pastikan ada pemasukan yang akan datang untuk menutupi defisit ini.` });
    }

    if (countBelumGaji > 0) {
      insights.push({ id: 'payroll', type: 'warning', text: `Ada **${countBelumGaji} karyawan** yang belum digaji (**Rp ${fmt(totalGajiBelum)}**). ${balances.total < totalGajiBelum ? '⚠️ Saldo kas saat ini TIDAK CUKUP untuk membayar gaji!' : 'Saldo kas masih mencukupi, segera proses.'}` });
    }

    if (countHutangJatuhTempo > 0) {
      insights.push({ id: 'hutang-lewat', type: 'alert', text: `🚨 **Perhatian:** Ada **${countHutangJatuhTempo} pengajuan pembelian** yang sudah **lewat jatuh tempo**! Segera lakukan pembayaran untuk menjaga reputasi dengan vendor.` });
    } else if (countHutangMendekati > 0) {
      insights.push({ id: 'hutang-dekat', type: 'warning', text: `Ada **${countHutangMendekati} pengajuan pembelian** yang akan jatuh tempo dalam waktu kurang dari 3 hari. Tolong dipersiapkan pembayarannya ya.` });
    }

    // ── Smart Insight: Deteksi Surplus Produksi ──
    const surplusTasks = (allTasks || []).filter(t => 
      t.status === 'completed' && 
      Number(t.completed_quantity) > Number(t.target_quantity) && 
      Number(t.target_quantity) > 0
    );
    if (surplusTasks.length > 0) {
      const topSurplus = surplusTasks[0];
      const diff = Number(topSurplus.completed_quantity) - Number(topSurplus.target_quantity);
      insights.push({
        id: `surplus-${topSurplus.id}`,
        type: 'warning',
        text: `📦 **Surplus Produksi Terdeteksi:** PO **${topSurplus.po_brand || 'PO'}** (${topSurplus.product_name || topSurplus.task_name}) selesai dengan hasil **${topSurplus.completed_quantity} pcs** (Target SPK: ${topSurplus.target_quantity} pcs, surplus **+${diff} pcs**). Segera sesuaikan invoice agar tagihan sesuai barang keluar!`
      });
    }

    // ── Smart Insight: Uang Masuk Menunggu Konfirmasi Finance ──
    if (pendingPayments.length > 0) {
      const totalPending = pendingPayments.reduce((s, p) => s + (Number(p.jumlah) || 0), 0);
      insights.push({
        id: 'pending-payments-alert',
        type: 'info',
        text: `💳 Ada **${pendingPayments.length} pembayaran invoice** sebesar **Rp ${fmt(totalPending)}** yang menunggu verifikasi Finance untuk resmi dicatat ke saldo kas.`
      });
    }

    // ── Smart Insight: Surat Jalan dengan Surplus Kuantitas (Lebih dari Invoice) ──
    const unconfirmedSurplusSJ = suratJalanList.filter(sj => sj.countSurplus > 0 && !sj.isConfirmedFinance);
    if (unconfirmedSurplusSJ.length > 0) {
      const topSJ = unconfirmedSurplusSJ[0];
      insights.push({
        id: `sj-surplus-${topSJ.id}`,
        type: 'alert',
        text: `🚚 **Surat Jalan Produksi Melebihi Invoice:** PO **${topSJ.brand_name}** dikirim **${topSJ.totalShipped} pcs** (Target SPK: ${topSJ.invoiceQty} pcs, surplus **+${topSJ.totalSurplusQty} pcs** / **+Rp ${fmt(topSJ.totalSurplusNominal)}**). Silakan verifikasi dan sesuaikan invoice agar tagihan sesuai barang keluar!`
      });
    }

    // ── Smart Insight: Reject Produksi Belum Dikonfirmasi ──
    if (unconfirmedRejects.length > 0) {
      insights.push({
        id: 'rejects-alert',
        type: 'warning',
        text: `⚠️ Ada **${unconfirmedRejects.length} item reject dari tim produksi** yang menunggu konfirmasi Finance untuk ditentukan pembebanan biayanya.`
      });
    }

    if (insights.length <= 1) {
      insights.push({ id: 'all-ok', type: 'info', text: `Semua indikator keuangan berada di zona aman. Tidak ada tindakan mendesak yang diperlukan saat ini. 🎉` });
    }

    return insights;
  }, [healthScore, marginPct, totalUangMasuk, totalPiutang, countPiutang, runwayMonths, mutasiMasukBulanIni, mutasiKeluarBulanIni, countBelumGaji, totalGajiBelum, balances.total, countHutangJatuhTempo, countHutangMendekati, countBelumDibayar, totalBelumDibayar, allTasks, pendingPayments, unconfirmedRejects, suratJalanList]);

  const HealthRing = ({ score }) => {
    const radius = 54;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    const color = getHealthRingColor(score);
    return (
      <div className="relative w-36 h-36 mx-auto">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 128 128">
          <circle cx="64" cy="64" r={radius} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="10" />
          <circle cx="64" cy="64" r={radius} fill="none" stroke="white" strokeWidth="10" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} className="transition-all duration-1000" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-black text-white">{score}</span>
          <span className="text-[9px] font-bold text-white/80 uppercase tracking-widest">/ 100</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8 pb-16 text-[#302621]">
      <div>
        <h1 className="text-xl md:text-3xl font-black text-slate-800 tracking-tight">Dashboard Keuangan</h1>
        <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 md:mt-1">Pantau kesehatan keuangan real-time: arus kas, margin aktual, piutang, dan kewajiban.</p>
      </div>

      <AsaAssistant 
        insights={asaInsights} 
        memberName={currentMember?.full_name?.split(' ')[0]} 
        title="Asa Financial Analyst"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <Card className={`border-0 overflow-hidden shadow-xl bg-gradient-to-br ${getHealthBg(healthScore)} text-white rounded-[2rem]`}>
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <HeartPulse className="w-5 h-5" />
              <h3 className="font-black text-sm uppercase tracking-wider">Kesehatan Keuangan</h3>
            </div>
            <HealthRing score={healthScore} />
            <div className="text-center mt-4">
              <p className="text-xl font-black">{getHealthLabel(healthScore)}</p>
              <p className="text-[10px] opacity-80 font-medium mt-1">Berdasarkan margin, arus kas, piutang & kewajiban</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/60 bg-white/80  shadow-sm col-span-1 lg:col-span-2 rounded-[2rem]">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4 md:mb-5">
              <Activity className="w-4 h-4 text-[#8C6208]" />
              <h3 className="font-black text-sm text-slate-800">Kondisi Keuangan Real-time</h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
              <div className="bg-slate-50 rounded-xl p-3 md:p-4 border border-slate-100">
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Uang Masuk Aktual</p>
                <p className="text-base md:text-lg font-black text-emerald-600">Rp {fmt(totalUangMasuk)}</p>
                <p className="text-[9px] text-slate-400 mt-1">dari semua invoice</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 md:p-4 border border-slate-100">
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total HPP / Modal</p>
                <p className="text-base md:text-lg font-black text-rose-600">Rp {fmt(totalHPP)}</p>
                <p className="text-[9px] text-slate-400 mt-1">biaya produksi</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 md:p-4 border border-slate-100">
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Margin Keuntungan</p>
                <p className={`text-base md:text-lg font-black ${totalMargin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {totalMargin >= 0 ? '+' : ''}Rp {fmt(totalMargin)}
                </p>
                <p className={`text-[9px] mt-1 font-bold ${marginPct >= 30 ? 'text-emerald-500' : marginPct >= 0 ? 'text-amber-500' : 'text-rose-500'}`}>
                  {marginPct.toFixed(1)}% dari pembayaran
                </p>
              </div>
              <div className={`rounded-xl p-3 md:p-4 border ${totalPiutang > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100'}`}>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Piutang Belum Lunas</p>
                <p className={`text-base md:text-lg font-black ${totalPiutang > 0 ? 'text-amber-600' : 'text-slate-400'}`}>Rp {fmt(totalPiutang)}</p>
                <p className="text-[9px] text-slate-400 mt-1">{countPiutang} invoice tertunggak</p>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="flex justify-between items-center mb-2">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Rasio Margin vs Modal</p>
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${
                  marginPct >= 30 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 
                  marginPct >= 0 ? 'bg-amber-50 text-amber-700 border-amber-200' : 
                  'bg-rose-50 text-rose-700 border-rose-200'
                }`}>
                  {marginPct >= 30 ? '✓ Sehat' : marginPct >= 0 ? '⚠ Perlu Perhatian' : '✗ Defisit'}
                </span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
                <div className="h-full bg-rose-400 rounded-l-full transition-all" style={{ width: `${totalUangMasuk > 0 ? Math.min(100, (totalHPP / totalUangMasuk * 100)) : (totalHPP > 0 ? 100 : 0)}%` }} />
                <div className="h-full bg-emerald-400 rounded-r-full transition-all" style={{ width: `${totalUangMasuk > 0 ? Math.max(0, 100 - (totalHPP / totalUangMasuk * 100)) : 0}%` }} />
              </div>
              <div className="flex justify-between mt-1.5 text-[9px] font-semibold text-slate-400">
                <span>Modal (HPP): {totalUangMasuk > 0 ? ((totalHPP / totalUangMasuk) * 100).toFixed(0) : (totalHPP > 0 ? 100 : 0)}%</span>
                <span>Laba Bersih: {marginPct.toFixed(1)}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <Card className="border-white/60 bg-white/80  shadow-sm overflow-hidden rounded-[2rem]">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shadow-sm">
                <Wallet className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-black text-sm text-slate-800">Arus Kas Bulan Ini</h3>
                <p className="text-[10px] text-slate-500 font-medium">Berdasarkan mutasi rekening tercatat</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-end mb-1">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1">
                    <ArrowUpRight className="w-3 h-3 text-emerald-500" /> Mutasi Masuk
                  </p>
                  <p className="font-black text-emerald-600">Rp {fmt(mutasiMasukBulanIni)}</p>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${Math.max(mutasiMasukBulanIni, mutasiKeluarBulanIni) > 0 ? (mutasiMasukBulanIni / Math.max(mutasiMasukBulanIni, mutasiKeluarBulanIni) * 100) : 0}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between items-end mb-1">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1">
                    <ArrowDownRight className="w-3 h-3 text-rose-500" /> Mutasi Keluar
                  </p>
                  <p className="font-black text-rose-600">Rp {fmt(mutasiKeluarBulanIni)}</p>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-rose-500 rounded-full transition-all" style={{ width: `${Math.max(mutasiMasukBulanIni, mutasiKeluarBulanIni) > 0 ? (mutasiKeluarBulanIni / Math.max(mutasiMasukBulanIni, mutasiKeluarBulanIni) * 100) : 0}%` }} />
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between items-center">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Net Cash Flow</p>
                <p className={`font-black ${(mutasiMasukBulanIni - mutasiKeluarBulanIni) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {(mutasiMasukBulanIni - mutasiKeluarBulanIni) >= 0 ? '+' : ''}Rp {fmt(mutasiMasukBulanIni - mutasiKeluarBulanIni)}
                </p>
              </div>

              <div className={`p-3 rounded-xl border text-xs font-semibold flex items-center gap-2 ${
                runwayMonths >= 3 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                runwayMonths >= 1 ? 'bg-amber-50 border-amber-200 text-amber-800' :
                'bg-rose-50 border-rose-200 text-rose-800'
              }`}>
                <Landmark className="w-4 h-4 shrink-0" />
                <span>Dana operasional cukup untuk <strong>~{runwayMonths.toFixed(1)} bulan</strong> ke depan berdasarkan pengeluaran saat ini</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/60 bg-white/80  shadow-sm rounded-[2rem] overflow-hidden">
          <CardContent className="p-0">
            <div className="px-4 md:px-6 py-3 md:py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-black text-sm text-slate-800 flex items-center gap-2">
                <Landmark className="w-4 h-4 text-[#8C6208]" /> Saldo Kas Utama
              </h3>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Total: Rp {fmt(balances.total)}</p>
            </div>
            <div className="divide-y divide-slate-100">
              <div className="p-3 md:p-4 flex justify-between items-center hover:bg-slate-50 transition-colors">
                <div>
                  <p className="font-bold text-sm text-slate-800">{accounts.bca?.name || 'BCA'}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{accounts.bca?.number || '-'}</p>
                </div>
                <p className="font-black text-slate-800">Rp {fmt(balances.bca)}</p>
              </div>
              <div className="p-3 md:p-4 flex justify-between items-center hover:bg-slate-50 transition-colors">
                <div>
                  <p className="font-bold text-sm text-slate-800">{accounts.mandiri?.name || 'Mandiri'}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{accounts.mandiri?.number || '-'}</p>
                </div>
                <p className="font-black text-slate-800">Rp {fmt(balances.mandiri)}</p>
              </div>
              <div className="p-3 md:p-4 flex justify-between items-center hover:bg-slate-50 transition-colors">
                <div>
                  <p className="font-bold text-sm text-slate-800">{accounts.bni?.name || 'BNI'}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{accounts.bni?.number || '-'}</p>
                </div>
                <p className="font-black text-slate-800">Rp {fmt(balances.bni)}</p>
              </div>
            </div>

            <div className="px-4 md:px-6 py-3 md:py-4 border-t border-slate-100 bg-slate-50/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="font-bold text-xs text-slate-700">Status Penggajian</span>
                </div>
                {countBelumGaji > 0 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
                      {countBelumGaji} belum gaji · Rp {fmt(totalGajiBelum)}
                    </span>
                    <Link to="/office/finance/payroll">
                      <Button size="sm" className="h-7 px-3 text-[10px] bg-blue-600 hover:bg-blue-700 rounded-lg">
                        Bayar
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Semua sudah digaji
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/60 bg-white/80  shadow-sm rounded-[2rem] overflow-hidden lg:col-span-2">
          <CardContent className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <h3 className="font-black text-sm text-slate-800 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" /> Arus Kas per Rekening
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold text-slate-500 mr-2">PERIODE:</span>
                {['Hari ini', '7 Hari', '30 Hari', 'Bulan Ini', 'Semua', 'Custom'].map(p => (
                  <Button 
                    key={p} 
                    variant={cashFlowPeriod === p ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCashFlowPeriod(p)}
                    className={`h-8 text-xs rounded-full transition-colors ${cashFlowPeriod === p ? 'bg-indigo-600 hover:bg-indigo-700 text-white border-transparent' : 'text-slate-600 border-slate-200 bg-white hover:bg-slate-50'}`}
                  >
                    {p}
                  </Button>
                ))}
              </div>
            </div>

            {cashFlowPeriod === 'Custom' && (
              <div className="flex items-center gap-3 mb-6 p-3 bg-slate-50 rounded-xl border border-slate-100 w-fit">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase">Dari Tanggal</label>
                  <input type="date" className="block text-sm bg-white border border-slate-200 rounded px-2 py-1 outline-none focus:border-indigo-400 mt-1" value={cashFlowDateStart} onChange={e => setCashFlowDateStart(e.target.value)} />
                </div>
                <div className="mt-5 text-slate-400 font-bold">-</div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase">Sampai Tanggal</label>
                  <input type="date" className="block text-sm bg-white border border-slate-200 rounded px-2 py-1 outline-none focus:border-indigo-400 mt-1" value={cashFlowDateEnd} onChange={e => setCashFlowDateEnd(e.target.value)} />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['bca', 'mandiri', 'bni'].map(key => {
                const acc = accounts[key];
                if (!acc) return null;
                const cf = cashFlowPerAccount[key];
                const net = cf.masuk - cf.keluar;
                return (
                  <div key={key} className="p-4 rounded-2xl border border-slate-100 bg-white hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-bold text-slate-800">{acc.name || key.toUpperCase()}</p>
                        <p className="text-[10px] text-slate-400">{acc.number || '-'}</p>
                      </div>
                      <span className={`text-xs font-black px-2 py-1 rounded-md ${net >= 0 ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>
                        {net >= 0 ? '+' : ''}Rp {fmt(net)}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-xs p-2 bg-slate-50 rounded-lg">
                        <span className="flex items-center gap-1 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                          <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" /> Masuk
                        </span>
                        <span className="font-black text-emerald-600">Rp {fmt(cf.masuk)}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs p-2 bg-slate-50 rounded-lg">
                        <span className="flex items-center gap-1 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                          <ArrowDownRight className="w-3.5 h-3.5 text-rose-500" /> Keluar
                        </span>
                        <span className="font-black text-rose-600">Rp {fmt(cf.keluar)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/60 bg-white/80  shadow-sm rounded-[2rem] overflow-hidden lg:col-span-2">
          <CardContent className="p-0">
            <div className="px-4 md:px-6 py-3 md:py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-black text-sm text-slate-800 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-emerald-600" /> Uang Masuk Invoice Menunggu Konfirmasi
              </h3>
              <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">{pendingPayments.length} Pembayaran</p>
            </div>
            
            {pendingPayments.length === 0 ? (
               <div className="p-8 text-center text-slate-500 text-sm font-medium">
                 Tidak ada pembayaran yang menunggu konfirmasi.
               </div>
            ) : (
               <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
                 {pendingPayments.map((pay) => (
                   <div key={pay.id} className="p-4 md:p-6 flex flex-col sm:flex-row gap-4 justify-between sm:items-center hover:bg-slate-50/50 transition-colors">
                     <div>
                       <p className="text-xs font-bold text-slate-800 mb-1">{pay.invoice_name}</p>
                       <p className="text-[10px] text-slate-500">
                         {pay.tanggal_bayar} • Diinput oleh <span className="font-medium">{pay.diinput_oleh}</span>
                       </p>
                       <p className="text-[10px] text-slate-500 mt-1">
                         Tujuan awal: <span className="font-medium">{pay.bank_tujuan}</span>
                         {pay.catatan && ` • Catatan: ${pay.catatan}`}
                       </p>
                     </div>
                     <div className="flex items-center gap-4 justify-between sm:justify-end">
                       <p className="text-sm font-black text-emerald-600 whitespace-nowrap">+ Rp {fmt(pay.jumlah)}</p>
                       <Button 
                         onClick={() => {
                           setSelectedPendingPayment(pay);
                           setApprovalModalOpen(true);
                         }}
                         className="h-8 px-4 text-[10px] font-bold bg-emerald-100 hover:bg-emerald-200 text-emerald-700 shadow-none cursor-pointer"
                       >
                         Konfirmasi
                       </Button>
                     </div>
                   </div>
                 ))}
               </div>
            )}
          </CardContent>
        </Card>

        {/* ── CARD: KONFIRMASI SURAT JALAN & PENGIRIMAN DARI PRODUKSI ── */}
        <Card className="border-white/60 bg-white/80 shadow-sm rounded-[2rem] overflow-hidden lg:col-span-2">
          <CardContent className="p-0">
            <div className="px-4 md:px-6 py-3 md:py-5 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 bg-slate-50/50">
              <div>
                <h3 className="font-black text-sm text-slate-800 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-indigo-600" /> Surat Jalan & Pengiriman dari Produksi
                </h3>
                <p className="text-[10px] text-slate-500 font-medium mt-0.5">
                  Cek kesesuaian jumlah barang riil di Surat Jalan dengan tagihan di Invoice
                </p>
              </div>
              <div className="flex items-center gap-2">
                {suratJalanList.filter(s => s.countSurplus > 0 && !s.isConfirmedFinance).length > 0 ? (
                  <span className="text-[10px] font-black text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full animate-pulse">
                    ⚠️ {suratJalanList.filter(s => s.countSurplus > 0 && !s.isConfirmedFinance).length} Perlu Penyesuaian Invoice
                  </span>
                ) : (
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Semua Sesuai
                  </span>
                )}
              </div>
            </div>

            {suratJalanList.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm font-medium">
                Belum ada data pengiriman atau Surat Jalan dari tim produksi.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[360px] overflow-y-auto">
                {suratJalanList.map((sj) => {
                  const hasSurplus = sj.countSurplus > 0;
                  return (
                    <div key={sj.id} className={`p-4 md:p-5 flex flex-col lg:flex-row gap-4 justify-between lg:items-center transition-colors ${hasSurplus && !sj.isConfirmedFinance ? 'bg-amber-50/20 hover:bg-amber-50/40' : 'hover:bg-slate-50/50'}`}>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold text-slate-900">{sj.brand_name} <span className="text-slate-500 font-normal">({sj.product_name})</span></p>
                          <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200">
                            #{sj.order_number}
                          </span>
                          {sj.totalShipmentsInPO > 1 && (
                            <span className="text-[10px] font-extrabold bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full">
                              Surat Jalan #{sj.shipmentNumber}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                          <span>PIC: <strong className="text-slate-700">{sj.pic_marketing}</strong></span>
                          <span>•</span>
                          <span>Tgl Kirim: {sj.shipped_date ? new Date(sj.shipped_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</span>
                          <span>•</span>
                          <span>Via: <strong>{sj.currentShipment?.shipping_via || sj.shipments[0]?.shipping_via || 'Ekspedisi'}</strong></span>
                        </p>
                      </div>

                      {/* Middle: Qty & Variants Status */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 bg-white p-2.5 rounded-xl border border-slate-200 text-xs shadow-2xs">
                        <div className="flex items-center gap-3">
                          <div className="text-center px-2">
                            <p className="text-[9px] font-bold text-slate-400 uppercase">Invoice Awal</p>
                            <p className="font-bold text-slate-700">{sj.invoiceQty} pcs</p>
                          </div>
                          <span className="text-slate-300 font-light">→</span>
                          <div className="text-center px-2">
                            <p className="text-[9px] font-bold text-slate-400 uppercase">{sj.totalShipmentsInPO > 1 ? `Kirim SJ #${sj.shipmentNumber}` : 'Surat Jalan'}</p>
                            <p className="font-black text-indigo-700">{sj.totalShipmentsInPO > 1 ? `${sj.shipmentQty} pcs` : `${sj.totalShipped} pcs`}</p>
                          </div>
                        </div>

                        <div className="sm:pl-2 sm:border-l sm:border-slate-100 w-full sm:w-auto">
                          {sj.isMatch ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-200">
                              <CheckCircle2 className="w-3 h-3" /> {sj.variantsBreakdown?.length > 1 ? `Semua ${sj.variantsBreakdown.length} Varian Pas` : 'Pas'}
                            </span>
                          ) : (
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1 flex-wrap">
                                {sj.countSurplus > 0 && (
                                  <span className="inline-flex items-center gap-1 text-[9px] font-black text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded border border-amber-300">
                                    <TrendingUp className="w-3 h-3 text-amber-700" /> {sj.countSurplus} Varian Lebih (+{sj.totalSurplusQty} pcs)
                                  </span>
                                )}
                                {sj.countPartial > 0 && (
                                  <span className="inline-flex items-center gap-1 text-[9px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                                    <Package className="w-3 h-3 text-blue-600" /> {sj.countPartial} Varian Sebagian
                                  </span>
                                )}
                              </div>
                              {sj.totalSurplusNominal > 0 && (
                                <p className="text-[9px] font-bold text-emerald-600">+Rp {fmt(sj.totalSurplusNominal)} tagihan</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right: 3 Dedicated Action Buttons */}
                      <div className="flex items-center gap-1.5 justify-end">
                        {/* 1. Tanda Ceklis / Konfirmasi (Membuka Modal Asa AI - Gambar 2) */}
                        {hasSurplus && !sj.isConfirmedFinance ? (
                          <Button
                            size="sm"
                            className="h-8 px-2.5 sm:px-3 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-xs gap-1.5"
                            onClick={() => setSelectedSuratJalanForSync(sj)}
                            title="Verifikasi & Sesuaikan Invoice"
                          >
                            <Sparkles className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Sesuaikan</span>
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="h-8 px-2.5 sm:px-3 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-xs gap-1.5"
                            onClick={() => setSelectedSuratJalanForSync(sj)}
                            title="Verifikasi Surat Jalan"
                          >
                            <Check className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Konfirmasi</span>
                          </Button>
                        )}

                        {/* 2. Tanda Mata (Membuka Print / PDF Surat Jalan - Gambar 3) */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900 border-slate-200 hover:bg-slate-100 rounded-xl bg-white shadow-2xs"
                          onClick={() => setPrintSuratJalanData({ order: sj.productionOrder, shipment: sj.shipments?.[0] })}
                          title="Lihat Cetak Surat Jalan"
                        >
                          <Eye className="w-4 h-4 text-slate-700" />
                        </Button>

                        {/* 3. Tanda Paling Kanan (Mengarahkan ke Invoice Terkait) */}
                        <Link
                          to={`/office/finance/invoices?search=${encodeURIComponent(sj.linkedPO?.id || (sj.order_number ? String(sj.order_number).replace(/^PO-/i, 'INV-') : '') || sj.brand_name)}`}
                          title="Buka Invoice Terkait"
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900 border-slate-200 hover:bg-slate-100 rounded-xl bg-white shadow-2xs"
                          >
                            <ExternalLink className="w-3.5 h-3.5 text-slate-700" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-white/60 bg-white/80  shadow-sm rounded-[2rem] overflow-hidden lg:col-span-2">
          <CardContent className="p-0">
            <div className="px-4 md:px-6 py-3 md:py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-black text-sm text-slate-800 flex items-center gap-2">
                <Package2 className="w-4 h-4 text-[#8C6208]" /> Pengajuan Pembelian
              </h3>
              <div className="flex flex-col sm:flex-row justify-end items-end sm:items-center gap-1 sm:gap-4">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Tempo Belum Lunas: Rp {fmt(totalHutangTempo)}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Menunggu Bayar: Rp {fmt(totalBelumDibayar)}</p>
              </div>
            </div>
            <div className="p-4 md:p-6 text-center space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                <div className="bg-rose-50 text-rose-700 px-4 py-3 rounded-xl border border-rose-100 flex-1">
                  <p className="text-2xl font-black">{countHutangJatuhTempo}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest mt-1">Lewat Tempo</p>
                </div>
                <div className="bg-amber-50 text-amber-700 px-4 py-3 rounded-xl border border-amber-100 flex-1">
                  <p className="text-2xl font-black">{countHutangMendekati}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest mt-1">Dekat Tempo</p>
                </div>
                <div className="bg-blue-50 text-blue-700 px-4 py-3 rounded-xl border border-blue-100 flex-1">
                  <p className="text-2xl font-black">{countBelumDibayar}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest mt-1">Menunggu Bayar</p>
                </div>
              </div>
              <Link to="/office/finance/purchasing-requests" className="block">
                <Button className="w-full h-12 bg-slate-800 hover:bg-slate-900 text-white shadow-lg shadow-slate-900/20 gap-2">
                  Lihat Semua Pengajuan <ArrowUpRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/60 bg-white/80  shadow-sm rounded-[2rem] overflow-hidden lg:col-span-2">
          <CardContent className="p-0">
            <div className="px-4 md:px-6 py-3 md:py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-black text-sm text-slate-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600" /> Reject Produksi Menunggu Konfirmasi
              </h3>
              <p className="text-[10px] font-bold text-rose-500 uppercase tracking-widest">{unconfirmedRejects.length} Item</p>
            </div>
            
            {unconfirmedRejects.length === 0 ? (
               <div className="p-8 text-center text-slate-500 text-sm font-medium">
                 Tidak ada item reject yang menunggu konfirmasi.
               </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                {unconfirmedRejects.map((rj) => (
                  <div key={rj.id} className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:bg-slate-50 transition-colors">
                    <div>
                      <p className="font-bold text-sm text-slate-800">{rj.itemName} <span className="text-rose-600">({rj.qty} pcs)</span></p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Tugas: {rj.taskName}</p>
                      {rj.notes && <p className="text-[10px] text-slate-400 italic mt-1">"{rj.notes}"</p>}
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="w-full sm:w-auto gap-1 text-[10px] h-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                        onClick={() => handleConfirmReject(rj)}
                        disabled={confirmingRejectId === rj.id}
                      >
                        <Check className="w-3 h-3" /> {confirmingRejectId === rj.id ? 'Loading...' : 'Konfirmasi'}
                      </Button>
                      <Link to="/office/finance/reject-tracking" className="w-full sm:w-auto">
                        <Button size="sm" variant="outline" className="w-full sm:w-auto h-8 text-[10px]">
                          Detail
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="p-4 border-t border-slate-100 bg-slate-50/30 text-center">
              <Link to="/office/finance/reject-tracking" className="text-xs font-bold text-slate-500 hover:text-slate-800">
                Lihat Semua Laporan Reject &rarr;
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {approvalModalOpen && selectedPendingPayment && (
        <PaymentApprovalModal 
          isOpen={approvalModalOpen}
          onClose={() => setApprovalModalOpen(false)}
          pendingPayment={selectedPendingPayment}
          bankAccounts={accounts}
          isConfirming={isConfirming}
          onConfirm={async (data) => {
            setIsConfirming(true);
            try {
              const selectedBankName = data.bank_tujuan || selectedPendingPayment.bank_tujuan || 'BCA';

              if (selectedPendingPayment.invoice_type === 'client_po') {
                const targetPO = clientPOs.find(p => p.id === selectedPendingPayment.invoice_id) || await base44.entities.ClientPO.get(selectedPendingPayment.invoice_id);
                if (targetPO) {
                  const riwayat = [...(targetPO.riwayatPembayaran || [])];
                  let matched = null;
                  if (selectedPendingPayment.paymentIndex !== undefined && riwayat[selectedPendingPayment.paymentIndex]) {
                    matched = riwayat[selectedPendingPayment.paymentIndex];
                  } else {
                    matched = riwayat.find(r => (r.jumlah === selectedPendingPayment.jumlah || Number(r.jumlah) === Number(selectedPendingPayment.jumlah)) && r.status_konfirmasi === 'Menunggu Konfirmasi Finance');
                  }
                  if (matched) {
                    matched.status_konfirmasi = 'Dikonfirmasi Finance';
                    matched.bank_tujuan = selectedBankName;
                  }

                  const validPayments = riwayat.filter(p => p.status_konfirmasi !== 'Menunggu Konfirmasi Finance');
                  const totalBayar = validPayments.reduce((s, p) => s + (Number(p.jumlah) || 0), 0);
                  const prodJual = (targetPO.products || targetPO.items || []).reduce((s, p) => s + (p.total_harga_jual || p.subtotal || 0), 0);
                  const legJual = (targetPO.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
                  const nominalPPN = targetPO.ppn_mode === 'exclude' ? ((prodJual + legJual) * (parseFloat(targetPO.ppn_percentage) || 0) / 100) : 0;
                  const totalTagihan = prodJual + legJual + (targetPO.shippingCost || 0) + nominalPPN;
                  const statusBayar = totalBayar >= totalTagihan && totalTagihan > 0 ? 'LUNAS' : (totalBayar > 0 ? 'DP ' + Math.round((totalBayar/totalTagihan)*100) + '%' : 'BELUM DIBAYAR');

                  await base44.entities.ClientPO.update(targetPO.id, {
                    riwayatPembayaran: riwayat,
                    statusBayar: statusBayar,
                    jumlahDibayar: totalBayar,
                    paymentMethod: selectedBankName
                  });
                }
              } else if (selectedPendingPayment.invoice_type === 'hpp') {
                const targetHpp = riwayatList.find(r => r.id === selectedPendingPayment.invoice_id) || await base44.entities.RiwayatProduksi.get(selectedPendingPayment.invoice_id);
                if (targetHpp) {
                  const riwayat = [...(targetHpp.riwayat_pembayaran || [])];
                  let matched = null;
                  if (selectedPendingPayment.paymentIndex !== undefined && riwayat[selectedPendingPayment.paymentIndex]) {
                    matched = riwayat[selectedPendingPayment.paymentIndex];
                  } else {
                    matched = riwayat.find(r => (r.jumlah === selectedPendingPayment.jumlah || Number(r.jumlah) === Number(selectedPendingPayment.jumlah)) && r.status_konfirmasi === 'Menunggu Konfirmasi Finance');
                  }
                  if (matched) {
                    matched.status_konfirmasi = 'Dikonfirmasi Finance';
                    matched.bank_tujuan = selectedBankName;
                  }

                  const validPayments = riwayat.filter(p => p.status_konfirmasi !== 'Menunggu Konfirmasi Finance');
                  const totalBayar = validPayments.reduce((s, p) => s + (Number(p.jumlah) || 0), 0);
                  const prodJual = targetHpp.total_harga_jual_semua || 0;
                  const nominalPPN = targetHpp.ppn_mode === 'exclude' ? (prodJual * (parseFloat(targetHpp.ppn_percentage) || 0) / 100) : 0;
                  const totalTagihan = prodJual + nominalPPN;
                  const statusBayar = totalBayar >= totalTagihan && totalTagihan > 0 ? 'Sudah Lunas' : (totalBayar > 0 ? 'Belum Lunas' : 'Belum Bayar');

                  await base44.entities.RiwayatProduksi.update(targetHpp.id, {
                    riwayat_pembayaran: riwayat,
                    status_bayar: statusBayar,
                    jumlah_dibayar: totalBayar
                  });
                }
              }

              // Create verified AccountMutation if mutation doesn't exist yet, or update existing mutation
              if (selectedPendingPayment.mutation_id) {
                await base44.entities.AccountMutation.update(selectedPendingPayment.mutation_id, {
                  status: 'verified',
                  account: selectedBankName,
                  type: 'masuk',
                  amount: selectedPendingPayment.jumlah,
                  description: `[Pembayaran Verified] Invoice: ${selectedPendingPayment.invoice_name}${selectedPendingPayment.catatan ? ' - ' + selectedPendingPayment.catatan : ''}`,
                  coa_allocations: data.coa_allocations
                });
              } else {
                await base44.entities.AccountMutation.create({
                  account: selectedBankName,
                  type: 'masuk',
                  amount: selectedPendingPayment.jumlah,
                  date: selectedPendingPayment.tanggal_bayar || new Date().toLocaleDateString('id-ID'),
                  description: `[Pembayaran Verified] Invoice: ${selectedPendingPayment.invoice_name}${selectedPendingPayment.catatan ? ' - ' + selectedPendingPayment.catatan : ''}`,
                  reference_id: selectedPendingPayment.invoice_id,
                  status: 'verified',
                  invoice_id: selectedPendingPayment.invoice_id,
                  invoice_type: selectedPendingPayment.invoice_type,
                  invoice_name: selectedPendingPayment.invoice_name,
                  jumlah: selectedPendingPayment.jumlah,
                  tanggal_bayar: selectedPendingPayment.tanggal_bayar,
                  catatan: selectedPendingPayment.catatan,
                  diinput_oleh: selectedPendingPayment.diinput_oleh,
                  bank_tujuan: selectedBankName,
                  coa_allocations: data.coa_allocations || [
                    {
                      account_id: '4-40001',
                      amount: selectedPendingPayment.jumlah,
                      notes: 'Penerimaan Pembayaran Invoice'
                    }
                  ]
                });
              }

              toast.success('Pembayaran berhasil dikonfirmasi dan masuk ke Kas Rekening!');
              queryClient.invalidateQueries({ queryKey: ['client-pos'] });
              queryClient.invalidateQueries({ queryKey: ['riwayat-produksi'] });
              queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
              queryClient.invalidateQueries({ queryKey: ['pending-payments'] });
              setApprovalModalOpen(false);
            } catch(e) {
              toast.error('Gagal konfirmasi: ' + (e.message || 'Error'));
            } finally {
              setIsConfirming(false);
            }
          }}
        />
      )}

      {/* ── MODAL: ASA AI KONFIRMASI PENYESUAIAN INVOICE DARI SURAT JALAN ── */}
      {selectedSuratJalanForSync && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-xl w-full shadow-2xl border border-amber-200/60 overflow-hidden animate-in fade-in zoom-in-95 duration-200 my-8">
            {/* Header with Asa AI theme */}
            <div className="p-5 md:p-6 bg-gradient-to-br from-[#FFFDF3] via-[#FAF5E6] to-[#F3EAD3] border-b border-[#E6D5B8]/60 flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#8C6208] text-white flex items-center justify-center shrink-0 shadow-md shadow-amber-900/20 border-2 border-white">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-black text-slate-900">
                    {selectedSuratJalanForSync.countSurplus > 0 ? "Asa AI Penyesuaian Invoice (Surplus)" : "Asa AI Verifikasi Surat Jalan"}
                  </h3>
                  <span className={`text-[9px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full ${selectedSuratJalanForSync.countSurplus > 0 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                    {selectedSuratJalanForSync.countSurplus > 0 ? "Surplus Produksi" : "Pengiriman Sesuai"}
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  {selectedSuratJalanForSync.countSurplus > 0 ? (
                    asaGaya === 'gaul'
                      ? `Halo ${asaNickname}! Asa mendeteksi tim produksi mengirim barang lebih banyak dari target invoice (Surplus +${selectedSuratJalanForSync.totalSurplusQty} pcs). Mau di-update ke invoice klien gak?`
                      : `Halo ${asaNickname}. Terdapat varian dengan pengiriman melebihi kuantitas invoice semula (+${selectedSuratJalanForSync.totalSurplusQty} pcs). Silakan sesuaikan invoice agar tagihan sesuai fisik barang keluar.`
                  ) : (
                    `Halo ${asaNickname}. Pengiriman Surat Jalan ini aman dan tidak melebihi target invoice. Silakan verifikasi untuk menyelesaikan catatan pemeriksaan.`
                  )}
                </p>
              </div>
              <button 
                onClick={() => setSelectedSuratJalanForSync(null)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-white/80 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 md:p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Tracking Information Card */}
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 text-xs space-y-2">
                <p className="font-bold text-[10px] text-slate-400 uppercase tracking-widest">Detail Pelacakan Invoice & PO</p>
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div>
                    <span className="text-slate-500 text-[10px]">Nomor Invoice:</span>
                    <p className="font-bold text-slate-800">#{selectedSuratJalanForSync.invoice_id}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[10px]">Brand / Klien:</span>
                    <p className="font-bold text-slate-800">{selectedSuratJalanForSync.brand_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[10px]">PIC Marketing:</span>
                    <p className="font-bold text-slate-800">{selectedSuratJalanForSync.pic_marketing}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[10px]">Tanggal Surat Jalan:</span>
                    <p className="font-bold text-slate-800">{selectedSuratJalanForSync.shipped_date ? new Date(selectedSuratJalanForSync.shipped_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</p>
                  </div>
                </div>
              </div>

              {/* Multi-Variant Detailed Breakdown Table */}
              {selectedSuratJalanForSync.variantsBreakdown?.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 uppercase tracking-wider px-1">
                    <span>Rincian Status per Varian ({selectedSuratJalanForSync.variantsBreakdown.length} Varian)</span>
                    <span className="flex items-center gap-2">
                      {selectedSuratJalanForSync.countMatch > 0 && (
                        <span className="inline-flex items-center gap-1 text-emerald-600">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" /> {selectedSuratJalanForSync.countMatch} Pas
                        </span>
                      )}
                      {selectedSuratJalanForSync.countSurplus > 0 && (
                        <span className="inline-flex items-center gap-1 text-amber-600">
                          <TrendingUp className="w-3 h-3 text-amber-600" /> {selectedSuratJalanForSync.countSurplus} Lebih
                        </span>
                      )}
                      {selectedSuratJalanForSync.countPartial > 0 && (
                        <span className="inline-flex items-center gap-1 text-blue-600 font-bold">
                          <Package className="w-3 h-3 text-blue-600" /> {selectedSuratJalanForSync.countPartial} Sebagian
                        </span>
                      )}
                    </span>
                  </div>
                  
                  <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-2xs max-h-52 overflow-y-auto divide-y divide-slate-100 text-xs">
                    {selectedSuratJalanForSync.variantsBreakdown.map((vb, idx) => (
                      <div key={idx} className={`p-2.5 flex items-center justify-between gap-3 ${vb.status === 'surplus' ? 'bg-amber-50/40' : vb.status === 'partial' ? 'bg-blue-50/30' : 'hover:bg-slate-50/50'}`}>
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-slate-900 truncate text-[11px]">{vb.variant_name}</p>
                          <p className="text-[10px] text-slate-500">
                            Invoice: <span className="font-semibold">{vb.invoice_qty} pcs</span> → Surat Jalan: <span className="font-bold text-slate-800">{vb.shipped_qty} pcs</span>
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          {vb.status === 'match' ? (
                            <span className="inline-flex items-center gap-1 text-[9px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                              <Check className="w-3 h-3 text-emerald-600" /> Pas (Aman)
                            </span>
                          ) : vb.status === 'surplus' ? (
                            <div>
                              <span className="inline-flex items-center gap-1 text-[9px] font-black text-amber-800 bg-amber-100 px-2 py-0.5 rounded border border-amber-300">
                                <TrendingUp className="w-3 h-3 text-amber-700" /> Surplus +{vb.diff} pcs
                              </span>
                              {vb.selisih_nominal > 0 && <p className="text-[9px] font-bold text-emerald-600 mt-0.5">+Rp {fmt(vb.selisih_nominal)}</p>}
                            </div>
                          ) : vb.status === 'partial' ? (
                            <div>
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                                <Package className="w-3 h-3 text-blue-600" /> Dikirim Sebagian ({vb.shipped_qty}/{vb.invoice_qty})
                              </span>
                              <p className="text-[9px] text-slate-400 mt-0.5">Sisa {Math.abs(vb.diff)} pcs</p>
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[9px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                              <Clock className="w-3 h-3 text-slate-400" /> Belum Dikirim
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Financial Calculation Box */}
              <div className="rounded-2xl p-4 border border-amber-200 bg-amber-50/50 space-y-2.5">
                <div className="flex justify-between items-center text-xs pb-2 border-b border-amber-200/60">
                  <span className="text-slate-600">Total Kuantitas Invoice Semula:</span>
                  <span className="font-bold text-slate-800">{selectedSuratJalanForSync.invoiceQty} pcs</span>
                </div>
                <div className="flex justify-between items-center text-xs pb-2 border-b border-amber-200/60">
                  <span className="text-slate-600">Total Kuantitas Riil Surat Jalan:</span>
                  <span className="font-black text-indigo-700">{selectedSuratJalanForSync.totalShipped} pcs</span>
                </div>
                
                {selectedSuratJalanForSync.totalSurplusNominal > 0 && (
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-xs font-bold text-amber-900">Total Tambahan Tagihan (Surplus):</span>
                    <span className="text-base font-black text-emerald-700">
                      +Rp {fmt(selectedSuratJalanForSync.totalSurplusNominal)}
                    </span>
                  </div>
                )}

                <div className="p-2.5 rounded-xl bg-white border border-amber-200 text-xs flex justify-between items-center">
                  <span className="text-slate-500 font-medium">
                    {selectedSuratJalanForSync.totalSurplusNominal > 0 ? "Estimasi Total Tagihan Baru:" : "Total Tagihan Invoice:"}
                  </span>
                  <span className="font-black text-slate-900 text-sm">
                    Rp {fmt(selectedSuratJalanForSync.totalSurplusNominal > 0 ? selectedSuratJalanForSync.newTagihan : selectedSuratJalanForSync.currentTagihan)}
                  </span>
                </div>
              </div>

              {selectedSuratJalanForSync.countSurplus > 0 && (
                <p className="text-[11px] text-slate-500 leading-relaxed italic text-center">
                  Apakah Anda ingin memperbarui kuantitas varian surplus dan menambah total tagihan di invoice klien?
                </p>
              )}
            </div>

            {/* Actions Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-col gap-2">
              {selectedSuratJalanForSync.countSurplus > 0 ? (
                <>
                  <Button
                    className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs rounded-xl shadow-md shadow-indigo-600/20 gap-2"
                    onClick={() => handleApplySuratJalanToInvoice(selectedSuratJalanForSync)}
                    disabled={isUpdatingInvoice}
                  >
                    {isUpdatingInvoice ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {isUpdatingInvoice ? "Memperbarui Invoice..." : `Ya, Update Invoice (+Rp ${fmt(selectedSuratJalanForSync.totalSurplusNominal)})`}
                  </Button>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1 h-9 text-xs border-slate-300 text-slate-700 hover:bg-slate-100 rounded-xl"
                      onClick={() => handleDismissSuratJalanSync(selectedSuratJalanForSync)}
                    >
                      Kelebihan Jadi Sampel (Jangan Update Invoice)
                    </Button>
                    <Button
                      variant="ghost"
                      className="h-9 text-xs text-slate-500 hover:text-slate-800 rounded-xl"
                      onClick={() => setSelectedSuratJalanForSync(null)}
                    >
                      Batal
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex gap-2">
                  <Button
                    className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-sm gap-2"
                    onClick={() => handleDismissSuratJalanSync(selectedSuratJalanForSync)}
                  >
                    <Check className="w-4 h-4" /> Tandai Surat Jalan Selesai Diperiksa
                  </Button>
                  <Button
                    variant="outline"
                    className="h-10 px-4 text-xs border-slate-300 text-slate-700 hover:bg-slate-100 rounded-xl"
                    onClick={() => setSelectedSuratJalanForSync(null)}
                  >
                    Tutup
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: SURAT JALAN PRINT / PDF PREVIEW (GAMBAR 3) ── */}
      {printSuratJalanData && (
        <SuratJalanPrint
          order={printSuratJalanData.order}
          shipment={printSuratJalanData.shipment}
          companySettings={companySettings}
          onClose={() => setPrintSuratJalanData(null)}
        />
      )}
    </div>
  );
}
